import { forwardRef, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `yyyy-mm-dd`, which is what the field stores and what every caller reads. */
const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * Parsed as a local date, never through `new Date('2026-10-01')` — that reads
 * a bare date as UTC, so anywhere east of Greenwich it lands on the previous
 * day. An ERP in India would have shown every date one off.
 */
function parse(value: unknown): Date | null {
  const s = String(value ?? '').slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

const human = (d: Date) => `${String(d.getDate()).padStart(2, '0')} ${SHORT[d.getMonth()]} ${d.getFullYear()}`;
const sameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** The 42 cells of a month grid, Sunday first, including the days either side. */
function monthGrid(year: number, month: number) {
  const first = new Date(year, month, 1);
  const start = new Date(year, month, 1 - first.getDay());
  return Array.from({ length: 42 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
}

/**
 * A date field in this product's own clothes.
 *
 * `<input type="date">` is drawn by the browser: a "dd-mm-yyyy" skeleton with
 * a grey glyph on Windows, a spinning wheel on a phone, something else on a
 * Mac. Next to fields the product had styled itself it was plainly someone
 * else's control, and on a form of five dates it was most of the form.
 *
 * It keeps a hidden native input and reports changes through it, so the
 * hundred and forty-odd places that read `e.target.value` keep working
 * untouched, and a form that submits the DOM still submits a date.
 */
export const DateField = forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & { error?: boolean }
>(({ className, value, defaultValue, onChange, disabled, error, min, max, placeholder, ...props }, ref) => {
  const innerRef = useRef<HTMLInputElement | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(String(value ?? defaultValue ?? ''));
  const [rect, setRect] = useState<{ top: number; left: number; width: number } | null>(null);

  const selected = useMemo(() => parse(current), [current]);
  const today = useMemo(() => new Date(), []);
  const [view, setView] = useState(() => selected ?? today);
  /** Which of the three panes is showing: the days, the months, or the years. */
  const [mode, setMode] = useState<'days' | 'months' | 'years'>('days');

  const lo = useMemo(() => parse(min), [min]);
  const hi = useMemo(() => parse(max), [max]);
  const blocked = (d: Date) => Boolean((lo && d < lo) || (hi && d > hi));

  function setRef(el: HTMLInputElement | null) {
    innerRef.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
  }

  // Follow the value when a parent resets or reloads the form.
  useEffect(() => {
    const el = innerRef.current;
    if (el && el.value !== current) setCurrent(el.value);
  });
  useEffect(() => { if (value !== undefined) setCurrent(String(value ?? '')); }, [value]);
  useEffect(() => { if (open) { setView(selected ?? today); setMode('days'); } }, [open]);

  function place() {
    const b = btnRef.current?.getBoundingClientRect();
    if (!b) return;
    const PANEL = 330;
    const openUp = window.innerHeight - b.bottom < PANEL && b.top > window.innerHeight - b.bottom;
    setRect({
      top: openUp ? Math.max(8, b.top - PANEL - 4) : b.bottom + 4,
      left: Math.min(b.left, Math.max(8, window.innerWidth - 300 - 8)),
      width: b.width,
    });
  }
  useLayoutEffect(() => { if (open) place(); }, [open]);
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      const t = e.target as Node;
      if (!btnRef.current?.contains(t) && !panelRef.current?.contains(t)) setOpen(false);
    }
    function away(e?: Event) {
      if (e && e.target instanceof Node && panelRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('scroll', away, true);
    window.addEventListener('resize', away);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('scroll', away, true);
      window.removeEventListener('resize', away);
    };
  }, [open]);

  function commit(next: string) {
    const el = innerRef.current;
    if (el) {
      el.value = next;
      onChange?.({ target: el, currentTarget: el } as unknown as React.ChangeEvent<HTMLInputElement>);
    }
    setCurrent(next);
  }

  function pick(d: Date) {
    if (blocked(d)) return;
    commit(iso(d));
    setOpen(false);
  }

  const days = monthGrid(view.getFullYear(), view.getMonth());

  /** Twelve years to a page, so a birth date is two or three clicks away. */
  const decadeStart = Math.floor(view.getFullYear() / 12) * 12;

  /** The arrows move by whatever the panel is currently showing. */
  const page = (dir: number) => setView((v) => (
    mode === 'days' ? new Date(v.getFullYear(), v.getMonth() + dir, 1)
      : mode === 'months' ? new Date(v.getFullYear() + dir, v.getMonth(), 1)
        : new Date(v.getFullYear() + dir * 12, v.getMonth(), 1)
  ));

  return (
    <div className={cn('relative', className)}>
      <input ref={setRef} type="date" value={current} onChange={() => {}} disabled={disabled}
        tabIndex={-1} aria-hidden className="sr-only" {...props} />

      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(true); }
        }}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={cn('input flex items-center justify-between pr-2.5 text-left', error && 'input-error')}
      >
        <span className={cn('truncate', !selected && 'text-ink-400 dark:text-ink-500')}>
          {selected ? human(selected) : (placeholder || 'Pick a date')}
        </span>
        <Calendar className="ml-2 h-4 w-4 shrink-0 text-ink-400" />
      </button>

      {open && rect && createPortal(
        <div ref={panelRef} role="dialog"
          style={{ position: 'fixed', top: rect.top, left: rect.left, width: 292, zIndex: 9999 }}
          className="rounded-md border border-border bg-card p-3 shadow-lg">
          {/*
            The month and the year are picked inside this panel, not from a
            dropdown of their own.

            They used to be the product's <Select>, which renders its menu in
            a portal on document.body — outside this panel. This panel closes
            itself when a click or a scroll lands outside it, so opening the
            month list and reaching for a month closed the calendar before the
            click arrived: the lists appeared, and neither scrolling nor
            clicking did anything. A date of birth is decades back, so that
            left no way to set one at all.
          */}
          <div className="mb-2 flex items-center justify-between">
            <button type="button" onClick={() => page(-1)}
              aria-label={mode === 'days' ? 'Previous month' : mode === 'months' ? 'Previous year' : 'Earlier years'}
              className="btn-ghost btn-icon btn-sm"><ChevronLeft size={16} /></button>

            <button type="button"
              onClick={() => setMode(mode === 'days' ? 'months' : mode === 'months' ? 'years' : 'days')}
              className="rounded px-2 py-1 text-[13px] font-medium hover:bg-accent"
              aria-label="Choose month or year">
              {mode === 'days' && `${MONTHS[view.getMonth()]} ${view.getFullYear()}`}
              {mode === 'months' && view.getFullYear()}
              {mode === 'years' && `${decadeStart} – ${decadeStart + 11}`}
            </button>

            <button type="button" onClick={() => page(1)}
              aria-label={mode === 'days' ? 'Next month' : mode === 'months' ? 'Next year' : 'Later years'}
              className="btn-ghost btn-icon btn-sm"><ChevronRight size={16} /></button>
          </div>

          {mode === 'months' && (
            <div className="grid grid-cols-3 gap-1">
              {MONTHS.map((m, i) => (
                <button key={m} type="button"
                  onClick={() => { setView(new Date(view.getFullYear(), i, 1)); setMode('days'); }}
                  className={cn('rounded py-2 text-[13px] hover:bg-accent',
                    i === view.getMonth() && 'bg-primary font-semibold text-primary-foreground hover:bg-primary')}>
                  {m.slice(0, 3)}
                </button>
              ))}
            </div>
          )}

          {mode === 'years' && (
            <div className="grid grid-cols-3 gap-1">
              {Array.from({ length: 12 }, (_, i) => decadeStart + i).map((y) => (
                <button key={y} type="button"
                  onClick={() => { setView(new Date(y, view.getMonth(), 1)); setMode('months'); }}
                  className={cn('rounded py-2 text-[13px] tabular hover:bg-accent',
                    y === view.getFullYear() && 'bg-primary font-semibold text-primary-foreground hover:bg-primary')}>
                  {y}
                </button>
              ))}
            </div>
          )}

          {mode === 'days' && (
          <div className="grid grid-cols-7 gap-0.5 text-center">
            {WEEKDAYS.map((w) => (
              <div key={w} className="py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{w.slice(0, 1)}</div>
            ))}
            {days.map((d) => {
              const outside = d.getMonth() !== view.getMonth();
              const off = blocked(d);
              const isSel = selected && sameDay(d, selected);
              return (
                <button
                  key={iso(d)}
                  type="button"
                  disabled={!!off}
                  onClick={() => pick(d)}
                  className={cn(
                    'h-8 rounded text-[13px] tabular',
                    off && 'cursor-not-allowed opacity-30',
                    !off && 'hover:bg-accent',
                    outside && !isSel && 'text-muted-foreground',
                    sameDay(d, today) && !isSel && 'font-semibold text-primary',
                    isSel && 'bg-primary font-semibold text-primary-foreground hover:bg-primary',
                  )}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>
          )}

          <div className="mt-2 flex items-center justify-between border-t border-border pt-2">
            <button type="button" onClick={() => { pick(today); setMode('days'); }} disabled={blocked(today)}
              className="btn-ghost btn-sm disabled:opacity-40">Today</button>
            <button type="button" onClick={() => { commit(''); setOpen(false); }}
              className="btn-ghost btn-sm">Clear</button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
});
DateField.displayName = 'DateField';
